/**
 * useShellPostures - the actor's postures that are not attacks.
 *
 * - DEFENSIVE STANCE lives in the cockpit's stance row (v8.236.0, lifted
 *   from the old drum plate): a Full-Action posture written as the Ds
 *   condition - synced, undoable, chip-visible. `defensiveStanceActive` is
 *   the row's readout; `handleToggleDefensiveStance` flips it.
 * - STAND UP (v8.304.0), the first `instant` action: it resolves on the
 *   spot, with no weapon and no opposition. Undoable like any decisive
 *   action, since it writes to a combatant's state.
 *
 * Extracted from DeathWatchSimulator; the log lines are unchanged.
 */
import { useCallback } from 'react';
import { condType } from '../engine/conditions';
import { resolveStand } from '../engine/actions/resolvers';
import { effectiveSheet } from '../engine/roster/stancedSheet';
import { hasCondition, infoLine } from './shellCombatant';

/** The stance's log line, rising or lowered. */
export function stanceText(wasActive) {
  return wasActive
    ? '⛨ Defensive Stance dropped.'
    : '⛨ Defensive Stance (Full Action) - no attacks until your next turn; foes take -20 WS; one extra Reaction.';
}

/** "<Name> gets up..." from the resolver's sentence, its first letter lowered. */
export function standText(actor, summary) {
  return `⬆ ${actor.name || 'The combatant'} ${summary.charAt(0).toLowerCase()}${summary.slice(1)}`;
}

/**
 * Stand up: a note when already standing, else an undoable write. Answers what it COST, for the pips (2026-10-26):
 * a Half, or nothing with Leap Up - read off the effective sheet, so a stance that grants the talent counts.
 */
function standUp(deps) {
  const { actor } = deps;
  const out = resolveStand(actor.state?.conditions ?? [], {
    hasLeapUp: (effectiveSheet(actor).talents ?? []).some((t) => t.talentId === 'leap_up'),
  });
  if (!out.wasProne) {
    deps.addRollNarrative(infoLine(`⬆ ${out.summary}`));
    return { cost: 'free' };
  }
  deps.capturePreActionSnapshot();
  deps.pushHistory();
  deps.updateCombatantState(actor.id, (st) => ({ ...st, conditions: out.conds }));
  deps.addRollNarrative(infoLine(standText(actor, out.summary)));
  return { cost: out.cost };
}

/**
 * @param {object} args - actor, actorConds (the actor's live conditions),
 *   updateCombatantState, addRollNarrative, capturePreActionSnapshot, pushHistory.
 * @returns {{ defensiveStanceActive: boolean, handleToggleDefensiveStance: Function, handleStand: Function }}
 */
export default function useShellPostures(args) {
  const { actor, actorConds, updateCombatantState, addRollNarrative, capturePreActionSnapshot, pushHistory } = args;
  const defensiveStanceActive = !!actor && hasCondition(actorConds, 'Ds');
  const handleToggleDefensiveStance = useCallback(() => {
    if (!actor) return;
    const active = defensiveStanceActive;
    updateCombatantState(actor.id, (st) => {
      const conds = (st.conditions ?? []).filter((c) => condType(c) !== 'Ds');
      if (!active) conds.push('Ds');
      return { ...st, conditions: conds };
    });
    addRollNarrative(infoLine(stanceText(active)));
  }, [actor, defensiveStanceActive, updateCombatantState, addRollNarrative]);
  const handleStand = useCallback(
    () =>
      actor ? standUp({ actor, updateCombatantState, addRollNarrative, capturePreActionSnapshot, pushHistory }) : null,
    [actor, updateCombatantState, addRollNarrative, capturePreActionSnapshot, pushHistory],
  );
  return { defensiveStanceActive, handleToggleDefensiveStance, handleStand };
}
